import React, { PropsWithChildren } from 'react';
import { Link } from 'react-router-dom';

import { Box, useGlobalZoomLevel, Icon, theme } from '@sbv-clients/ui-shared';

type SharedAuthLayoutProps = PropsWithChildren & {
  isCheckIcon?: boolean;
};

export const SharedAuthLayout = ({
  children,
  isCheckIcon = false,
}: SharedAuthLayoutProps) => {
  const zoom = useGlobalZoomLevel(1);

  return (
    <Box
      sx={{
        width: `${100 / zoom}vw`,
        minHeight: `${100 / zoom}vh`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.custom?.authPageGradient,
        py: 8,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '500px',
          mx: 3,
          borderRadius: '16px',
          bgcolor: theme.background.white,
          boxShadow: theme.custom?.authCardShadow,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            borderRadius: '16px 16px 0 0',
            background: `linear-gradient(90deg, ${theme.colors.primary.main}, ${theme.colors.gold})`,
          },
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          px={{ xs: 5, md: 10 }}
          pt={8}
          pb={{ xs: 6, md: 9 }}
          sx={{
            '& .MuiTypography-h1': {
              fontSize: '24px',
              lineHeight: '32px',
              fontWeight: 700,
              letterSpacing: '-0.01em',
              color: theme.custom?.brandNavy,
            },
            '& .MuiTypography-body1': {
              fontSize: '14px',
              lineHeight: '21px',
              color: theme.custom?.headerInfoText,
            },
            '& a': { textDecoration: 'none' },
            '& a .MuiTypography-root': {
              fontSize: '14px',
              fontWeight: 600,
              color: theme.colors.primary.main,
            },
            '& a:hover .MuiTypography-root': { textDecoration: 'underline' },
            '& .MuiFormHelperText-root': {
              marginTop: '6px',
              fontSize: '12px',
              color: theme.custom?.tableHeaderText,
            },
          }}
        >
          <Link to="/">
            <Icon
              name={'LogoSVG'}
              customSizes={{ width: '170px', height: '52px' }}
            />
          </Link>
          {isCheckIcon && (
            <Box
              aria-hidden
              sx={{
                mt: 6,
                width: 52,
                height: 52,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                background: theme.custom?.stepDoneBg,
                color: theme.custom?.stepDoneText,
              }}
            >
              <Box
                component="svg"
                viewBox="0 0 24 24"
                sx={{
                  width: 26,
                  height: 26,
                  fill: 'none',
                  stroke: 'currentColor',
                  strokeWidth: 2.2,
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                }}
              >
                <path d="m5 12.5 4.5 4.5L19 7.5" />
              </Box>
            </Box>
          )}
          <Box width="100%" mt={6}>
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SharedAuthLayout;
